import React, { useState, useEffect } from "react";
import { DatePicker, Button, message, Row, Col, Select, Spin } from "antd";
import API from "../../../api";
import urls from "../../../api/urls";
import moment from "moment";
import { dateFormat_ymd, dateFormat_dmy } from "../../../utils";
import { DownloadOutlined, FilterOutlined, FileTextOutlined } from "@ant-design/icons";
import TableContainer from "../../../components/TableContainer";
import { generateDynamicColumns } from "../../../utils/columnGenerator";
import useReportFilters from "../../../hooks/useReportFilters";
import "./style.scss";

const { RangePicker } = DatePicker;
const { Option } = Select;

const disabledDate = (current) => {
  return current && current > moment().endOf("day");
};

const DeliveryMISReportNew = () => {
  const {
    filters,
    debouncedFilters,
    handleFilterChange,
    handlePageChange,
    pageNum,
    pageSize,
    setPageNum,
  } = useReportFilters({
    hub_id: undefined,
    cp_id: undefined,
  }, 500);

  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [total, setTotal] = useState(0);
  const [returnedRecords, setReturnedRecords] = useState("-");
  const [dateRange, setDateRange] = useState({
    startDate: moment().subtract(1, "days"),
    endDate: moment(),
  });
  const [hubs, setHubs] = useState([]);
  const [channelPartners, setChannelPartners] = useState([]);
  const [refreshSchedule, setRefreshSchedule] = useState(null);

  useEffect(() => {
    fetchHubs();
    fetchChannelPartners();
    fetchRefreshSchedule();
  }, []);

  const fetchRefreshSchedule = async () => {
    try {
      const res = await API.Api(urls.REFRESH_SCHEDULE_DELIVERY).get();
      if (res?.response?.data?.data?.[0]) {
        setRefreshSchedule(res.response.data.data[0]);
      }
    } catch (error) {
      console.error("Error fetching refresh schedule:", error);
    }
  };

  useEffect(() => {
    fetchData(pageNum, pageSize);
  }, [dateRange, pageNum, pageSize, debouncedFilters]);

  const fetchHubs = async () => {
    try {
      const result = await API.Api(urls.GET_HUB_LISTS).get();
      const response = result?.response?.data;
      if (result?.status && response) {
        setHubs(Array.isArray(response) ? response : (response.data || []));
      }
    } catch (error) {
      console.error("Error fetching hubs:", error);
    }
  };

  const fetchChannelPartners = async () => {
    try {
      const result = await API.Api(urls.DRS_STATUS_CHANNEL_PARTNER_LIST).get();
      const response = result?.response?.data;
      if (result?.status && response) {
        setChannelPartners(Array.isArray(response) ? response : (response.data || []));
      }
    } catch (error) {
      console.error("Error fetching channel partners:", error);
    }
  };

  const fetchData = async (page, size) => {
    if (!dateRange.startDate || !dateRange.endDate) return;

    setLoading(true);
    try {
      const body = {
        user_name: "laxminandan",
        user_email: "laxminandan.handique@shreemaruti.com",
      };

      const queryParams = new URLSearchParams({
        fromDate_date: dateRange.startDate.format(dateFormat_ymd),
        toDate_date: dateRange.endDate.format(dateFormat_ymd),
        page: page.toString(),
        size: size.toString(),
      });

      if (debouncedFilters.hub_id) {
        queryParams.append("premise_id", debouncedFilters.hub_id);
      }
      if (debouncedFilters.cp_id) {
        queryParams.append("channel_partner", debouncedFilters.cp_id);
      }

      const url = `${urls.DELIVERY_MIS_REPORT_NEW}?${queryParams.toString()}`;
      const res = await API.Api(url).postCustomHeader(body, {
        Authorization:
          "Bearer 542735477f9c35f653ef6b3bf9d9cbbf577f8f0a75c45f4221a7590de8ae4f90",
      });

      if (res?.response?.data) {
        const responseData = res.response.data;
        setTableData(responseData.data || []);
        setTotal(responseData.total_records || 0);
        setReturnedRecords(responseData.returned_records || 0);
      }
    } catch (error) {
      console.error("Error fetching report data:", error);
      message.error("Failed to fetch report data");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!dateRange.startDate || !dateRange.endDate) {
      message.warning("Please select a date range");
      return;
    }

    const queryParams = new URLSearchParams({
      fromDate: dateRange.startDate.format(dateFormat_ymd),
      toDate: dateRange.endDate.format(dateFormat_ymd),
    });

    if (debouncedFilters.hub_id) {
      queryParams.append("premiseId", debouncedFilters.hub_id);
    }
    if (debouncedFilters.cp_id) {
      queryParams.append("channelPartner", debouncedFilters.cp_id);
    }

    const downloadUrl = `${urls.DELIVERY_MIS_REPORT_NEW_DOWNLOAD}?${queryParams.toString()}`;
    window.open(downloadUrl, "_blank");
  };

  const handleDateChange = (dates) => {
    if (dates) {
      setDateRange({ startDate: dates[0], endDate: dates[1] });
    } else {
      setDateRange({ startDate: null, endDate: null });
    }
    setPageNum(1);
  };

  const staticColumns = [
    {
      title: "Shipments Outscanned",
      dataIndex: "shipments_outscanned",
      key: "shipments_outscanned",
      align: "center",
      render: (text, record) => {
        if (text === 0 || text === "0") return text;
        return (
          <span
            style={{ color: "#1890ff", cursor: "pointer", fontWeight: "500" }}
            onClick={() => {
              const queryParams = new URLSearchParams({
                type: "mis-outscan",
                fromDate_outscan_date: record.date,
                toDate_outscan_date: record.date,
                from_hub_id: record.premise_id,
                to_channel_partner: record.channel_partner,
              });
              window.open(
                `/drs-clickable-detail?${queryParams.toString()}`,
                "_blank",
              );
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "Inscanned Same Day",
      dataIndex: "shipments_inscanned_on_same_day",
      key: "shipments_inscanned_on_same_day",
      align: "center",
      render: (text, record) => {
        if (text === 0 || text === "0") return text;
        return (
          <span
            style={{ color: "#1890ff", cursor: "pointer", fontWeight: "500" }}
            onClick={() => {
              const queryParams = new URLSearchParams({
                type: "mis-inscan-same-day",
                fromDate_outscan_date: record.date,
                toDate_outscan_date: record.date,
                from_hub_id: record.premise_id,
                to_channel_partner: record.channel_partner,
              });
              window.open(
                `/drs-clickable-detail?${queryParams.toString()}`,
                "_blank",
              );
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "Not Inscanned Same Day",
      dataIndex: "shipments_not_inscanned_on_same_day",
      key: "shipments_not_inscanned_on_same_day",
      align: "center",
      render: (text, record) => {
        if (text === 0 || text === "0") return text;
        return (
          <span
            style={{ color: "#1890ff", cursor: "pointer", fontWeight: "500" }}
            onClick={() => {
              const queryParams = new URLSearchParams({
                type: "mis-not-inscan-same-day",
                fromDate_outscan_date: record.date,
                toDate_outscan_date: record.date,
                from_hub_id: record.premise_id,
                to_channel_partner: record.channel_partner,
              });
              window.open(
                `/drs-clickable-detail?${queryParams.toString()}`,
                "_blank",
              );
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "OFD Same Day",
      dataIndex: "shipments_ofd_on_same_day",
      key: "shipments_ofd_on_same_day",
      align: "center",
      render: (text, record) => {
        if (text === 0 || text === "0") return text;
        return (
          <span
            style={{ color: "#1890ff", cursor: "pointer", fontWeight: "500" }}
            onClick={() => {
              const queryParams = new URLSearchParams({
                type: "mis-ofd-same-day",
                fromDate_outscan_date: record.date,
                toDate_outscan_date: record.date,
                from_hub_id: record.premise_id,
                to_channel_partner: record.channel_partner,
              });
              window.open(
                `/drs-clickable-detail?${queryParams.toString()}`,
                "_blank",
              );
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "Delivered Same Day",
      dataIndex: "shipments_delivered_on_same_day",
      key: "shipments_delivered_on_same_day",
      align: "center",
      render: (text, record) => {
        if (text === 0 || text === "0") return text;
        return (
          <span
            style={{ color: "#1890ff", cursor: "pointer", fontWeight: "500" }}
            onClick={() => {
              const queryParams = new URLSearchParams({
                type: "mis-delivered-same-day",
                fromDate_outscan_date: record.date,
                toDate_outscan_date: record.date,
                from_hub_id: record.premise_id,
                to_channel_partner: record.channel_partner,
              });
              window.open(
                `/drs-clickable-detail?${queryParams.toString()}`,
                "_blank",
              );
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "Undelivered Same Day",
      dataIndex: "shipments_undelivered_on_same_day",
      key: "shipments_undelivered_on_same_day",
      align: "center",
      render: (text, record) => {
        if (text === 0 || text === "0") return text;
        return (
          <span
            style={{ color: "#1890ff", cursor: "pointer", fontWeight: "500" }}
            onClick={() => {
              const queryParams = new URLSearchParams({
                type: "mis-undelivered-same-day",
                fromDate_outscan_date: record.date,
                toDate_outscan_date: record.date,
                from_hub_id: record.premise_id,
                to_channel_partner: record.channel_partner,
              });
              window.open(
                `/drs-clickable-detail?${queryParams.toString()}`,
                "_blank",
              );
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "RTO OFD Same Day",
      dataIndex: "shipments_rto_ofd_on_same_day",
      key: "shipments_rto_ofd_on_same_day",
      align: "center",
      render: (text, record) => {
        if (text === 0 || text === "0") return text;
        return (
          <span
            style={{ color: "#1890ff", cursor: "pointer", fontWeight: "500" }}
            onClick={() => {
              const queryParams = new URLSearchParams({
                type: "mis-rto-ofd-same-day",
                fromDate_outscan_date: record.date,
                toDate_outscan_date: record.date,
                from_hub_id: record.premise_id,
                to_channel_partner: record.channel_partner,
              });
              window.open(
                `/drs-clickable-detail?${queryParams.toString()}`,
                "_blank",
              );
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "RTO Initiated Same Day",
      dataIndex: "shipments_rto_initiated_on_same_day",
      key: "shipments_rto_initiated_on_same_day",
      align: "center",
      render: (text, record) => {
        if (text === 0 || text === "0") return text;
        return (
          <span
            style={{ color: "#1890ff", cursor: "pointer", fontWeight: "500" }}
            onClick={() => {
              const queryParams = new URLSearchParams({
                type: "mis-rto-initiated-same-day",
                fromDate_outscan_date: record.date,
                toDate_outscan_date: record.date,
                from_hub_id: record.premise_id,
                to_channel_partner: record.channel_partner,
              });
              window.open(
                `/drs-clickable-detail?${queryParams.toString()}`,
                "_blank",
              );
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "RTO Delivered Same Day",
      dataIndex: "shipments_rto_delivered_on_same_day",
      key: "shipments_rto_delivered_on_same_day",
      align: "center",
      render: (text, record) => {
        if (text === 0 || text === "0") return text;
        return (
          <span
            style={{ color: "#1890ff", cursor: "pointer", fontWeight: "500" }}
            onClick={() => {
              const queryParams = new URLSearchParams({
                type: "mis-rto-delivered-same-day",
                fromDate_outscan_date: record.date,
                toDate_outscan_date: record.date,
                from_hub_id: record.premise_id,
                to_channel_partner: record.channel_partner,
              });
              window.open(
                `/drs-clickable-detail?${queryParams.toString()}`,
                "_blank",
              );
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "RTO Undelivered Same Day",
      dataIndex: "shipments_rto_undelivered_on_same_day",
      key: "shipments_rto_undelivered_on_same_day",
      align: "center",
      render: (text, record) => {
        if (text === 0 || text === "0") return text;
        return (
          <span
            style={{ color: "#1890ff", cursor: "pointer", fontWeight: "500" }}
            onClick={() => {
              const queryParams = new URLSearchParams({
                type: "mis-rto-undelivered-same-day",
                fromDate_outscan_date: record.date,
                toDate_outscan_date: record.date,
                from_hub_id: record.premise_id,
                to_channel_partner: record.channel_partner,
              });
              window.open(
                `/drs-clickable-detail?${queryParams.toString()}`,
                "_blank",
              );
            }}
          >
            {text}
          </span>
        );
      },
    },
  ];

  const columns = React.useMemo(
    () => generateDynamicColumns(tableData, staticColumns),
    [tableData, staticColumns],
  );

  return (
    <div className="delivery-mis-report-container">
      {/* ── Page Top Bar ── */}
      <div className="page-topbar">
        <div className="topbar-left">
          <span className="title-main">Delivery MIS Report</span>
          {refreshSchedule && (
            <div className="sync-indicators">
              <div className="sync-tag last-refresh">
                <span className="sync-label">Last Refresh</span>
                <span className="sync-time">
                  {moment(refreshSchedule.dag_last_completion_time_ist).format(
                    "DD-MM-YYYY | HH:mm:ss",
                  )}
                </span>
              </div>
              <div className="sync-tag next-sync">
                <span className="sync-label">Next Sync</span>
                <span className="sync-time">
                  {moment(refreshSchedule.dag_next_run_ist).format(
                    "DD-MM-YYYY | HH:mm:ss",
                  )}
                </span>
              </div>
            </div>
          )}
        </div>
        <div className="topbar-actions">
          <div className="filter-item">
            <span className="filter-label">Registration Date</span>
            <RangePicker
              value={[dateRange.startDate, dateRange.endDate]}
              format={dateFormat_dmy}
              onChange={handleDateChange}
              disabledDate={disabledDate}
              allowClear={false}
            />
          </div>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleDownload}
            className="download-btn"
          >
            Download
          </Button>
        </div>
      </div>

      {/* ── Summary Hero Band ── */}
      <div className="summary-hero-band">
        <div className="hero-left">
          <div className="hero-icon">
            <FileTextOutlined />
          </div>
          <div className="hero-text">
            <div className="hero-label">MIS Summary</div>
            <div className="hero-desc">
              Comprehensive delivery performance metrics and same-day efficiency
              analysis
            </div>
          </div>
        </div>
        <div className="hero-stats">
          <div className="hero-stat">
            <div className="hero-stat-value">
              {total ? total.toLocaleString() : "0"}
            </div>
            <div className="hero-stat-label">Total Records</div>
          </div>
          <div className="hero-divider" />
          <div className="hero-stat">
            <div className="hero-stat-value">
              {typeof returnedRecords === "number"
                ? returnedRecords.toLocaleString()
                : returnedRecords}
            </div>
            <div className="hero-stat-label">Showing</div>
          </div>
        </div>
      </div>

      {/* ── Filters Panel ── */}
      <div className="filters-panel">
        <div className="filters-header">
          <FilterOutlined className="filter-icon" />
          <span className="filters-title">Data Filters</span>
        </div>
        <div className="filters-body">
          <Row gutter={[16, 14]}>
            <Col xs={24} sm={12} md={8} lg={6} className="filter-col">
              <span className="filter-field-label">Hub</span>
              <Select
                allowClear
                showSearch
                placeholder="Select Hub"
                style={{ width: "100%" }}
                value={filters.hub_id}
                onChange={(value) => handleFilterChange("hub_id", value)}
                optionFilterProp="children"
              >
                {hubs.map((hub, idx) => {
                  const label = hub?.premiseName || hub?.hub_name || (typeof hub === "string" ? hub : "");
                  const value = hub?.premiseId || hub?.hub_id || (typeof hub === "string" ? hub : idx);
                  return (
                    <Option key={value || idx} value={value}>
                      {String(label || value || idx)}
                    </Option>
                  );
                })}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6} className="filter-col">
              <span className="filter-field-label">Channel Partner</span>
              <Select
                allowClear
                showSearch
                placeholder="Select CP"
                style={{ width: "100%" }}
                value={filters.cp_id}
                onChange={(value) => handleFilterChange("cp_id", value)}
                optionFilterProp="children"
              >
                {channelPartners.map((cp, idx) => {
                  const label = cp?.channel_partner || (typeof cp === "string" ? cp : "");
                  const value = cp?.cp_id || (typeof cp === "string" ? cp : idx);
                  return (
                    <Option key={value || idx} value={value}>
                      {String(label || value || idx)}
                    </Option>
                  );
                })}
              </Select>
            </Col>
          </Row>
        </div>
      </div>

      {/* ── Data Table ── */}
      <Spin spinning={loading}>
        <div className="table-panel">
          <TableContainer
            columns={columns}
            dataSource={tableData}
            pagination={{
              current: pageNum,
              pageSize: pageSize,
              total: total || 0,
              showSizeChanger: true,
              pageSizeOptions: ["50", "100", "150", "200"],
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
            }}
            onChange={(pagination) => handlePageChange(pagination.current, pagination.pageSize)}
            scroll={{ x: "max-content" }}
            rowKey={(record, index) =>
              `${record.date}_${record.channel_partner}_${index}`
            }
          />
        </div>
      </Spin>
    </div>
  );
};

export default DeliveryMISReportNew;
